import React, { useState } from 'react';

interface BioEditorProps {
  initialBio: string;
  onSave: (bio: string) => Promise<{ success: boolean; error?: string }>;
}

const BioEditor: React.FC<BioEditorProps> = ({ initialBio, onSave }) => {
  const [bio, setBio] = useState(initialBio);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const result = await onSave(bio);
      
      if (result.success) {
        setSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to save bio');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setBio(initialBio);
    setIsEditing(false);
    setError(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-medium text-gray-800">Artist Bio</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Edit Bio
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <label htmlFor="bio" className="sr-only">Artist Bio</label>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="w-full h-64 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Write your artist bio here..."
            aria-label="Artist Bio"
          />
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Bio'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
          
          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="text-green-500 text-sm">
              Bio saved successfully!
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-md">
          {bio ? (
            <div className="prose max-w-none">
              {bio.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No bio has been added yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default BioEditor;